/**
 * Production's pre-TRUS-762 version, kept verbatim for orgs WITHOUT the
 * `member_profile_v2` capability (they keep the previous member profile).
 * Do not add features here; remove once every org is on member_profile_v2.
 */
/**
 * MemberCaseEpisodes - Full episode management workspace for a member in a program.
 *
 * Layout:
 *   1. Episode context bar (case #, status, dates, actions) — compact header
 *   2. Episode profile — editable program-specific data, episode overrides
 *   3. Change history and past episodes — collapsible, with reopen action
 *
 * This is the case details view only. The case's activities, documents,
 * tasks, conversations and forms live in MemberProgramWorkspace's work bar;
 * this view used to repeat them as a second tab set over the same enrollment
 * (TRUS-762).
 *
 * The header shows the episode linked to the enrollment picked in the
 * workspace, so it never describes a different case from the work around it.
 * With no linked episode it falls back to the program's current episode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Clock, Plus, Archive, RotateCcw, ChevronDown, ChevronRight,
  User, Calendar, XCircle, AlertCircle, CheckCircle2, Settings, Pencil, History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { caseEpisodesAPI, CaseEpisode, EpisodeStatus, EpisodeUpdate, EpisodeAuditEntry } from '@/services/api/caseEpisodes';
import { membersAPI, ProfileField } from '@/services/api/members';

interface MemberCaseEpisodesProps {
  memberId: number;
  programId: number;
  cboId: number;
  enrollmentId?: number;
  onEpisodeChange?: (episode: CaseEpisode | null) => void;
  onNavigate?: (tab: string, id?: number) => void;
}

const STATUS_CONFIG: Record<EpisodeStatus, { label: string; color: string; icon: React.ReactNode }> = {
  NEW: { label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: <AlertCircle className="w-3 h-3" /> },
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: <CheckCircle2 className="w-3 h-3" /> },
  SUSPENDED: { label: 'Suspended', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: <Clock className="w-3 h-3" /> },
  COMPLETED: { label: 'Completed', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: <CheckCircle2 className="w-3 h-3" /> },
  CLOSED: { label: 'Closed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', icon: <XCircle className="w-3 h-3" /> },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400', icon: <Archive className="w-3 h-3" /> },
};

export default function MemberCaseEpisodes({
  memberId, programId, cboId, enrollmentId, onEpisodeChange,
}: MemberCaseEpisodesProps) {
  const [episodes, setEpisodes] = useState<CaseEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closingEpisodeId, setClosingEpisodeId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<EpisodeAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createData, setCreateData] = useState({
    service_period_start: '',
    service_period_end: '',
    authorized_duration_days: '',
    authorization_number: '',
  });

  // The episode shown in the header: the one linked to the picked enrollment,
  // else the program's current one. A picked past enrollment shows its own
  // (closed) case read-only instead of the current case's header.
  const linkedEpisode = enrollmentId ? episodes.find(e => e.enrollment_id === enrollmentId) : undefined;
  const currentEpisode = linkedEpisode ?? episodes.find(e => e.is_current);
  const isPastEpisode = !!currentEpisode && !currentEpisode.is_current;
  const archivedEpisodes = episodes.filter(e => e.id !== currentEpisode?.id);

  const loadEpisodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await caseEpisodesAPI.listEpisodes({
        member_id: memberId,
        program_id: programId,
        include_archived: true,
      });
      setEpisodes(data);
      const current = data.find(e => e.is_current);
      onEpisodeChange?.(current || null);
    } catch (err) {
      console.error('Failed to load episodes:', err);
    } finally {
      setLoading(false);
    }
  }, [memberId, programId]);

  useEffect(() => { loadEpisodes(); }, [loadEpisodes]);

  const handleCreateNew = async () => {
    setActionLoading(true);
    try {
      await caseEpisodesAPI.createEpisode({
        cbo_id: cboId,
        member_id: memberId,
        program_id: programId,
        service_period_start: createData.service_period_start || undefined,
        service_period_end: createData.service_period_end || undefined,
        authorized_duration_days: createData.authorized_duration_days
          ? Number(createData.authorized_duration_days) : undefined,
        authorization_number: createData.authorization_number || undefined,
      });
      setCreateDialogOpen(false);
      setCreateData({ service_period_start: '', service_period_end: '', authorized_duration_days: '', authorization_number: '' });
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to create episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const loadAuditLog = useCallback(async () => {
    if (!currentEpisode) return;
    setAuditLoading(true);
    try {
      const data = await caseEpisodesAPI.getEpisodeAuditLog(currentEpisode.id);
      setAuditLog(data);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [currentEpisode?.id]);

  const handleClose = async () => {
    if (!closingEpisodeId || !closeReason.trim()) return;
    setActionLoading(true);
    try {
      await caseEpisodesAPI.closeEpisode(closingEpisodeId, closeReason.trim());
      setCloseDialogOpen(false);
      setCloseReason('');
      setClosingEpisodeId(null);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to close episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async (episodeId: number) => {
    setActionLoading(true);
    try {
      await caseEpisodesAPI.reopenEpisode(episodeId);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to reopen episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async (episodeId: number, newStatus: EpisodeStatus) => {
    setActionLoading(true);
    try {
      await caseEpisodesAPI.updateEpisode(episodeId, { status: newStatus });
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to update episode status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const loadProfileFields = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const fields = await membersAPI.getProfileFields({ program_id: programId });
      setProfileFields(fields);
    } catch (err) {
      console.error('Failed to load profile fields:', err);
    } finally {
      setFieldsLoading(false);
    }
  }, [programId]);

  const openEditDialog = () => {
    const merged: Record<string, any> = {
      health_plan_member_id: currentEpisode?.health_plan_member_id || '',
      member_record_status: currentEpisode?.member_record_status || '',
      service_period_start: (currentEpisode as any)?.service_period_start || '',
      service_period_end: (currentEpisode as any)?.service_period_end || '',
      authorized_duration_days: (currentEpisode as any)?.authorized_duration_days || '',
      authorization_number: (currentEpisode as any)?.authorization_number || '',
    };
    // Merge program_specific_data and episode_profile_data into a flat map for field editing
    const psd = currentEpisode?.program_specific_data || {};
    const epd = currentEpisode?.episode_profile_data || {};
    Object.entries(psd).forEach(([k, v]) => { merged[`psd__${k}`] = v ?? ''; });
    Object.entries(epd).forEach(([k, v]) => { merged[`epd__${k}`] = v ?? ''; });
    setEditData(merged);
    setEditProfileOpen(true);
    if (profileFields.length === 0) loadProfileFields();
  };

  const handleSaveProfile = async () => {
    if (!currentEpisode) return;
    setActionLoading(true);
    try {
      // Reconstruct program_specific_data and episode_profile_data from flat editData
      const programSpecificUpdate: Record<string, any> = {};
      const episodeProfileUpdate: Record<string, any> = {};
      Object.entries(editData).forEach(([k, v]) => {
        if (k.startsWith('psd__')) programSpecificUpdate[k.slice(5)] = v;
        else if (k.startsWith('epd__')) episodeProfileUpdate[k.slice(5)] = v;
      });

      const update: EpisodeUpdate = {
        health_plan_member_id: editData.health_plan_member_id || undefined,
        member_record_status: editData.member_record_status || undefined,
        program_specific_data: programSpecificUpdate,
        episode_profile_data: episodeProfileUpdate,
        service_period_start: editData.service_period_start || undefined,
        service_period_end: editData.service_period_end || undefined,
        authorized_duration_days: editData.authorized_duration_days ? Number(editData.authorized_duration_days) : undefined,
        authorization_number: editData.authorization_number || undefined,
      };
      await caseEpisodesAPI.updateEpisode(currentEpisode.id, update);
      setEditProfileOpen(false);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to update episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-gray-500">
          Loading case episodes...
        </CardContent>
      </Card>
    );
  }

  // ========================================================================
  // Field input renderer based on field_type from profile-fields config
  // ========================================================================
  function renderFieldInput(field: ProfileField, value: any, onChange: (v: any) => void) {
    const fieldType = field.field_type;
    switch (fieldType) {
      case 'date':
      case 'fixed_date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text}
          />
        );
      case 'datetime':
        return (
          <Input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text}
          />
        );
      case 'int':
        return (
          <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder_text}
          />
        );
      case 'bool':
      case 'checkbox':
      case 'toggle':
        return (
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={value === true || value === 'true' || value === '1'}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">{value ? 'Yes' : 'No'}</span>
          </div>
        );
      case 'select':
      case 'radio':
        // For select/radio, we don't have field_options on ProfileField — fall through to text
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text || `Enter ${field.display_name.toLowerCase()}`}
          />
        );
      case 'time':
        return (
          <Input
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      default: // str, multi_select, etc.
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text || `Enter ${field.display_name.toLowerCase()}`}
          />
        );
    }
  }

  // ========================================================================
  // No current episode — show create prompt
  // ========================================================================
  if (!currentEpisode) {
    return (
      <div className="space-y-4">
        <Card className="border-dashed">
          <CardContent className="py-6 text-center">
            <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 mb-3">No active case episode for this program</p>
            <Button size="sm" onClick={() => setCreateDialogOpen(true)} disabled={actionLoading}>
              <Plus className="w-4 h-4 mr-1" />
              Create New Case
            </Button>
          </CardContent>
        </Card>

        {/* Past episodes still visible */}
        {archivedEpisodes.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Past Episodes ({archivedEpisodes.length})
            </button>
            {showHistory && renderArchivedList()}
          </div>
        )}
      </div>
    );
  }

  // ========================================================================
  // Full episode workspace
  // ========================================================================

  function renderArchivedList() {
    return (
      <div className="mt-3 space-y-2">
        {archivedEpisodes.map((ep) => (
          <Card key={ep.id} className="bg-gray-50 dark:bg-gray-900/50">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <div>
                    <p className="text-sm font-medium">
                      Case #{ep.case_number}
                      <span className="ml-1.5 text-xs text-gray-500">Ep. {ep.episode_number}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {ep.opened_date}{ep.closed_date ? ` - ${ep.closed_date}` : ''}
                      {ep.closed_reason && ` (${ep.closed_reason})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[ep.status].color}`}>
                    {STATUS_CONFIG[ep.status].label}
                  </span>
                  {(ep.status === 'CLOSED' || ep.status === 'ARCHIVED') && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleReopen(ep.id)} disabled={actionLoading}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const programSpecific = currentEpisode.program_specific_data || {};
  const profileOverrides = currentEpisode.episode_profile_data || {};

  return (
    <div className="space-y-4">
      {/* ================================================================
          EPISODE CONTEXT BAR — compact header with key info + actions
          ================================================================ */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            {/* Left: Case info */}
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-600 shrink-0" />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">Case #{currentEpisode.case_number}</span>
                  <span className="text-xs text-gray-500">Ep. {currentEpisode.episode_number}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[currentEpisode.status].color}`}>
                    {STATUS_CONFIG[currentEpisode.status].icon}
                    {STATUS_CONFIG[currentEpisode.status].label}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-0.5 text-xs text-gray-500 flex-wrap">
                  <span>Opened {currentEpisode.opened_date || '--'}</span>
                  {currentEpisode.assigned_to_name && (
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{currentEpisode.assigned_to_name}</span>
                  )}
                  {currentEpisode.payer_name && <span>Plan: {currentEpisode.payer_name}</span>}
                  {currentEpisode.health_plan_member_id && <span>HP ID: {currentEpisode.health_plan_member_id}</span>}
                  {(currentEpisode as any).service_period_start && (
                    <span>
                      Period: {(currentEpisode as any).service_period_start}
                      {(currentEpisode as any).service_period_end ? ` - ${(currentEpisode as any).service_period_end}` : ''}
                    </span>
                  )}
                  {(currentEpisode as any).authorization_number && (
                    <span>Auth #: {(currentEpisode as any).authorization_number}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Actions. A past case is read-only apart from Reopen. */}
            {isPastEpisode ? (
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant="secondary">Past case</Badge>
                {(currentEpisode.status === 'CLOSED' || currentEpisode.status === 'ARCHIVED') && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleReopen(currentEpisode.id)} disabled={actionLoading}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                  </Button>
                )}
              </div>
            ) : (
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Status transition */}
              {(currentEpisode.status === 'NEW' || currentEpisode.status === 'ACTIVE' || currentEpisode.status === 'SUSPENDED') && (
                <Select
                  value=""
                  onValueChange={(val) => {
                    if (val === 'CLOSE') {
                      setClosingEpisodeId(currentEpisode.id);
                      setCloseDialogOpen(true);
                    } else {
                      handleStatusUpdate(currentEpisode.id, val as EpisodeStatus);
                    }
                  }}
                >
                  <SelectTrigger className="w-[120px] h-7 text-xs">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentEpisode.status === 'NEW' && <SelectItem value="ACTIVE">Activate</SelectItem>}
                    {currentEpisode.status !== 'SUSPENDED' && <SelectItem value="SUSPENDED">Suspend</SelectItem>}
                    {currentEpisode.status === 'SUSPENDED' && <SelectItem value="ACTIVE">Resume</SelectItem>}
                    <SelectItem value="COMPLETED">Complete</SelectItem>
                    <SelectItem value="CLOSE">Close Case</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={openEditDialog}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCreateDialogOpen(true)} disabled={actionLoading}>
                <Plus className="w-3 h-3 mr-1" /> New Case
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => {
                setClosingEpisodeId(currentEpisode.id);
                setCloseDialogOpen(true);
              }} disabled={actionLoading}>
                <XCircle className="w-3 h-3 mr-1" /> Close
              </Button>
            </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ================================================================
          PROGRAM-SPECIFIC DATA — show key fields from the episode
          ================================================================ */}
      {(Object.keys(programSpecific).length > 0 || Object.keys(profileOverrides).length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(programSpecific).map(([key, val]) => (
            <Card key={key} className="bg-gray-50 dark:bg-gray-900/50">
              <CardContent className="py-2 px-3">
                <p className="text-[11px] text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-sm font-medium truncate">{String(val || '--')}</p>
              </CardContent>
            </Card>
          ))}
          {Object.entries(profileOverrides).map(([key, val]) => (
            <Card key={`po-${key}`} className="bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="py-2 px-3">
                <p className="text-[11px] text-blue-600 dark:text-blue-400 capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-sm font-medium truncate">{String(val || '--')}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!enrollmentId && (
        <Card className="border-dashed">
          <CardContent className="py-4 text-center text-sm text-gray-500">
            No linked enrollment found. Activities, documents, tasks, conversations, and forms
            will appear once the episode is linked to a program enrollment.
          </CardContent>
        </Card>
      )}

      {/* ================================================================
          EPISODE CHANGE HISTORY (audit trail)
          ================================================================ */}
      {currentEpisode && (
        <div>
          <Separator className="my-2" />
          <button
            type="button"
            onClick={() => {
              setShowAudit(!showAudit);
              if (!showAudit && auditLog.length === 0) loadAuditLog();
            }}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {showAudit ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Change History
          </button>
          {showAudit && (
            <div className="mt-3 space-y-2">
              {auditLoading ? (
                <p className="text-xs text-gray-500 py-2">Loading history...</p>
              ) : auditLog.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No changes recorded yet for this episode.</p>
              ) : (
                auditLog.map((entry) => (
                  <Card key={entry.id} className="bg-gray-50 dark:bg-gray-900/50">
                    <CardContent className="py-2 px-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">
                          {entry.performed_by_name || `User #${entry.performed_by}`}
                          <span className="text-gray-400 ml-1">{entry.action}</span>
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(entry.performed_at).toLocaleString()}
                        </span>
                      </div>
                      {entry.field_changes && Object.keys(entry.field_changes).length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {Object.entries(entry.field_changes).map(([field, change]) => (
                            <div key={field} className="text-[11px] text-gray-600 dark:text-gray-400">
                              <span className="font-medium capitalize">{field.replace(/_/g, ' ')}</span>:{' '}
                              <span className="line-through text-red-500/70">{String(change.old ?? '(empty)')}</span>
                              {' → '}
                              <span className="text-green-600 dark:text-green-400">{String(change.new ?? '(empty)')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ================================================================
          PAST EPISODES — collapsible
          ================================================================ */}
      {archivedEpisodes.length > 0 && (
        <div>
          <Separator className="my-2" />
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Past Episodes ({archivedEpisodes.length})
          </button>
          {showHistory && renderArchivedList()}
        </div>
      )}

      {/* ================================================================
          EDIT EPISODE PROFILE DIALOG
          ================================================================ */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Episode Details</DialogTitle>
            <DialogDescription>
              Update episode-specific fields. Changes here apply only to this case episode.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            {/* Core episode fields */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Case Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Health Plan Member ID</Label>
                  <Input
                    value={editData.health_plan_member_id || ''}
                    onChange={(e) => setEditData({ ...editData, health_plan_member_id: e.target.value })}
                    placeholder="e.g., HP12345678"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Member Record Status</Label>
                  <Select
                    value={editData.member_record_status || ''}
                    onValueChange={(val) => setEditData({ ...editData, member_record_status: val })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Returning">Returning</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Transferred">Transferred</SelectItem>
                      <SelectItem value="Discharged">Discharged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Service Period */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Service Period</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Service Period Start</Label>
                  <Input
                    type="date"
                    value={editData.service_period_start || ''}
                    onChange={(e) => setEditData({ ...editData, service_period_start: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Service Period End</Label>
                  <Input
                    type="date"
                    value={editData.service_period_end || ''}
                    onChange={(e) => setEditData({ ...editData, service_period_end: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Authorized Duration (days)</Label>
                  <Input
                    type="number"
                    value={editData.authorized_duration_days || ''}
                    onChange={(e) => setEditData({ ...editData, authorized_duration_days: e.target.value ? Number(e.target.value) : '' })}
                    placeholder="e.g., 365"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Authorization Number</Label>
                  <Input
                    value={editData.authorization_number || ''}
                    onChange={(e) => setEditData({ ...editData, authorization_number: e.target.value })}
                    placeholder="Payer auth reference"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Configured fields from profile-fields API, grouped by category */}
            {fieldsLoading ? (
              <div className="py-4 text-center text-sm text-gray-500">Loading fields...</div>
            ) : profileFields.length > 0 ? (
              (() => {
                // Group by category
                const grouped = profileFields
                  .filter(f => !f.is_hidden && !f.is_readonly)
                  .reduce<Record<string, ProfileField[]>>((acc, f) => {
                    const cat = f.category_name || 'Other';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(f);
                    return acc;
                  }, {});

                // Sort groups by first field's category_sort
                const sortedGroups = Object.entries(grouped).sort(
                  ([, a], [, b]) => (a[0]?.category_sort || 0) - (b[0]?.category_sort || 0)
                );

                return sortedGroups.map(([categoryName, fields]) => (
                  <div key={categoryName}>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{categoryName}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {fields
                        .sort((a, b) => (a.field_sort || 0) - (b.field_sort || 0))
                        .map(field => {
                          // Determine which JSONB bucket this field lives in:
                          // Core fields (identity, demographics, contact, address, insurance) → episode_profile_data
                          // Custom/program fields → program_specific_data
                          const prefix = field.is_core_field ? 'epd__' : 'psd__';
                          const dataKey = `${prefix}${field.field_key}`;
                          const value = editData[dataKey] ?? '';
                          const widthClass = field.display_width === 'full' ? 'col-span-2' : '';

                          return (
                            <div key={field.field_key} className={`space-y-1.5 ${widthClass}`}>
                              <Label className="text-xs">
                                {field.display_name}
                                {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              {field.help_text && (
                                <p className="text-[10px] text-gray-400">{field.help_text}</p>
                              )}
                              {renderFieldInput(field, value, (v) => setEditData({ ...editData, [dataKey]: v }))}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div className="text-sm text-gray-500 py-2">
                No additional fields configured for this program.
                <span className="block text-xs text-gray-400 mt-1">
                  Configure fields in Admin &gt; Member Fields to see them here.
                </span>
              </div>
            )}

            {/* Existing JSONB data not covered by configured fields */}
            {(() => {
              const configuredKeys = new Set(profileFields.map(f => f.field_key));
              const extraPsd = Object.entries(currentEpisode?.program_specific_data || {})
                .filter(([k]) => !configuredKeys.has(k));
              const extraEpd = Object.entries(currentEpisode?.episode_profile_data || {})
                .filter(([k]) => !configuredKeys.has(k));

              if (extraPsd.length === 0 && extraEpd.length === 0) return null;

              return (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Additional Fields</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {extraPsd.map(([key]) => (
                        <div key={`psd-${key}`} className="space-y-1.5">
                          <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                          <Input
                            value={editData[`psd__${key}`] ?? ''}
                            onChange={(e) => setEditData({ ...editData, [`psd__${key}`]: e.target.value })}
                          />
                        </div>
                      ))}
                      {extraEpd.map(([key]) => (
                        <div key={`epd-${key}`} className="space-y-1.5">
                          <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                          <Input
                            value={editData[`epd__${key}`] ?? ''}
                            onChange={(e) => setEditData({ ...editData, [`epd__${key}`]: e.target.value })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} disabled={actionLoading}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================
          CLOSE EPISODE DIALOG
          ================================================================ */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Case Episode</DialogTitle>
            <DialogDescription>
              Closing this case will archive it and make it read-only. You can reopen it later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="closeReason">Reason for closing</Label>
            <Input
              id="closeReason"
              placeholder="e.g., Services completed, member transferred..."
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleClose} disabled={!closeReason.trim() || actionLoading}>
              Close Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================
          CREATE NEW EPISODE DIALOG (with service period)
          ================================================================ */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Case Episode</DialogTitle>
            <DialogDescription>
              This will start a new service period with a fresh enrollment and workflow.
              The current episode will be archived.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Service Period Start</Label>
                <Input
                  type="date"
                  value={createData.service_period_start}
                  onChange={(e) => setCreateData({ ...createData, service_period_start: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Service Period End</Label>
                <Input
                  type="date"
                  value={createData.service_period_end}
                  onChange={(e) => setCreateData({ ...createData, service_period_end: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Authorized Duration (days)</Label>
                <Input
                  type="number"
                  value={createData.authorized_duration_days}
                  onChange={(e) => setCreateData({ ...createData, authorized_duration_days: e.target.value })}
                  placeholder="e.g., 365"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Authorization Number</Label>
                <Input
                  value={createData.authorization_number}
                  onChange={(e) => setCreateData({ ...createData, authorization_number: e.target.value })}
                  placeholder="Payer auth reference"
                />
              </div>
            </div>
            {currentEpisode && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Current episode (Case #{currentEpisode.case_number}) will be archived.
                  A new enrollment and program workflow will be created for the new episode.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateNew} disabled={actionLoading}>
              {actionLoading ? 'Creating...' : 'Create New Episode'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
